import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useCashflow } from "@/hooks/useAccounting";
import { buildMonthlySeries, categoryBreakdown, deriveKpis } from "@/lib/accounting/accountingView";
import { formatAccountingAmount } from "@/lib/accounting/format";
import { cn } from "@/lib/utils";

interface Props {
  period: { start_date: string; end_date: string };
}

/**
 * Onglet Trésorerie : KPI du mois sélectionné, graphique de cash-flow sur
 * 12 mois (barres in/out + net cumulé) et ventilations par catégorie et
 * méthode. Mobile-first : rangée de KPI scrollable, graphique pleine largeur.
 */
export function AccountingTreasuryTab({ period }: Props) {
  const { t } = useTranslation(["accounting"]);
  const { currency } = useCurrency();
  const fmt = (v: number) => formatAccountingAmount(v, currency);

  const { data, isLoading } = useCashflow(period);
  const kpis = useMemo(() => deriveKpis(data ?? null), [data]);
  const series = useMemo(() => (data ? buildMonthlySeries(data) : []), [data]);
  const categories = useMemo(() => categoryBreakdown(data?.summary.byCategory ?? {}), [data]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2 overflow-x-auto">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 min-w-[150px] flex-1 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-xl" />
      </div>
    );
  }

  const kpiCards = [
    { label: t("kpi.cashIn"), value: kpis.cashIn, className: "bg-emerald-50 dark:bg-emerald-950", accent: "text-emerald-600 dark:text-emerald-400" },
    { label: t("kpi.cashOut"), value: kpis.cashOut, className: "bg-red-50 dark:bg-red-950", accent: "text-red-600 dark:text-red-400" },
    { label: t("kpi.margin"), value: kpis.margin, className: "bg-indigo-50 dark:bg-indigo-950", accent: kpis.margin >= 0 ? "text-emerald-600" : "text-red-600" },
  ];

  return (
    <div className="space-y-4">
      {/* KPI — scrollables sur mobile */}
      <div className="flex gap-2 overflow-x-auto pb-1 md:grid md:grid-cols-3 md:gap-3 md:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">

        {kpiCards.map((k) => (
          <div key={k.label} className={cn("rounded-xl px-4 py-3 min-w-[150px] shrink-0 md:min-w-0", k.className)}>
            <div className="text-xs md:text-sm text-muted-foreground whitespace-nowrap">{k.label}</div>
            <div className={cn("text-lg md:text-2xl font-bold tabular-nums whitespace-nowrap", k.accent)}>
              {fmt(k.value)}
            </div>
          </div>
        ))}
      </div>

      {/* En attente / à payer sur la période */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs md:text-sm text-muted-foreground px-1">
        <span>{t("kpi.pendingIn")} : <b className="tabular-nums">{fmt(kpis.pendingIn)}</b></span>
        <span>{t("kpi.dueOut")} : <b className="tabular-nums">{fmt(kpis.dueOut)}</b></span>
      </div>

      {/* Cash-flow 12 mois */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base md:text-lg">{t("chart.title")}</CardTitle>
        </CardHeader>
        <CardContent className="pl-0 pr-2">
          <div className="h-64 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-40" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m: string) => m.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} width={56} />
                <Tooltip formatter={(value: number, name: string) => [fmt(value), name]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="cash_in" name={t("chart.cashIn")} fill="hsl(152 60% 42%)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cash_out" name={t("chart.cashOut")} fill="hsl(0 72% 55%)" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="cumul" name={t("chart.cumul")} stroke="hsl(243 60% 55%)" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="px-4 pb-1 text-[11px] text-muted-foreground">
            {t("chart.engagedHint")}
          </p>
        </CardContent>
      </Card>

      {/* Ventilations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("breakdown.byCategory")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">{t("breakdown.empty")}</p>
            )}
            {categories.map((c) => (
              <div key={c.category}>
                <div className="flex items-center justify-between text-sm">
                  <span>{t(`categories.${c.category}`)}</span>
                  <span className="tabular-nums font-medium">{fmt(c.amount)} <span className="text-muted-foreground">({c.pct}%)</span></span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${c.pct}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("breakdown.byMethod")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(["in", "out"] as const).map((dir) => {
              const entries = (Object.entries(data?.summary.byMethod[dir] ?? {}) as [string, number][])
                .sort((a, b) => b[1] - a[1]);
              return (
                <div key={dir}>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    {dir === "in" ? t("breakdown.inflows") : t("breakdown.outflows")}
                  </div>
                  {entries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">—</p>
                  ) : (
                    entries.map(([method, amount]) => (
                      <div key={method} className="flex items-center justify-between text-sm py-0.5">
                        <span>{t(`methods.${method}`, method)}</span>
                        <span className="tabular-nums">{fmt(amount)}</span>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
